import {
    Material,
    Color,
    isMaterial,
    isColor,
    ColorWithSubstrate,
    isColorWithSubstrate,
    QuiltedMaterial,
    isQuiltedMaterial,
    LayerData,
    PipingOrTape,
    Zipper,
    FabricLayer,
    Puller,
    Handle,
    MaterialInput,
    MeshWithInputs,
    AssetType,
    PIPING_SUBSTRATE_GLOSSY_ID,
    PIPING_SUBSTRATE_MATTE_ID,
    PULLER_SUBSTRATE_ID,
    fabricInput,
    colorInput,
    fabricOrColorInput,
    pipingMaterialInput,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/types"
import {getDescriptorsOfType, ImageInfo, InterfaceDescriptor, isNamedNode, MaterialInfo, TemplateInfo, TemplateNode, NumberInfo} from "@cm/template-nodes"
import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import {findSingleDescriptor} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/descriptors"
import {
    getAccessoryTemplateInputForBase,
    getPipingMaterialInputId,
    getPullerBaseIdFromZipperBaseId,
    getPullerPositionDescriptors,
    getPullerPositionInputId,
    getPullerTemplateInputIdFromPullerBase,
    getPullerTemplateInputIdFromZipperBase,
    getZipperBaseIdFromPullerBase,
    getZipperColorInputId,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/accessories"
import {getImageOrigin, getMaterialOrigin, getTemplateOrigin} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/parameter-storage"
import {resolveSlotMaterialOrColor} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/materials"
import {getTemplateId} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/asset-id"
import {getHandleTemplateInput} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/handles"

async function resolveTemplateInput(
    templateInputId: string,
    allDescriptors: InterfaceDescriptor<unknown>[],
    ctx: ExtensionContext,
): Promise<{templateInputId: string; templateId: string}> {
    const templateInput = findSingleDescriptor(getDescriptorsOfType(allDescriptors, TemplateInfo), templateInputId, "template input")
    const {value: templateNode} = getTemplateOrigin(templateInput, ctx)
    const templateId = await getTemplateId(templateNode, ctx)
    if (!templateId) throw new Error(`Could not determine template ID for template input ${templateInputId}`)
    return {templateInputId, templateId}
}

// Piping and tape take the same material types, e.g. the "Piping Left" / "Tape Left" inputs of handles.
// "tape" must be a whole word, so fabric inputs like "Tapestry" or "Tapered Border" are not affected.
function isPipingOrTapeMaterialInputName(name: string): boolean {
    const lowerCaseName = name.toLowerCase()
    return lowerCaseName.includes("piping") || /\btape\b/.test(lowerCaseName)
}

async function resolvePipingTapeMaterial(materialInput: MaterialInfo, context: ExtensionContext): Promise<Material | ColorWithSubstrate | undefined> {
    let materialValue: Material | ColorWithSubstrate | undefined = undefined
    const materialNode = getMaterialOrigin(materialInput, context).value
    if (materialNode) {
        const m = await resolveSlotMaterialOrColor(materialInput.props.id, materialNode, context)
        if (m && !isMaterial(m) && !isColorWithSubstrate(m))
            throw new Error(`Expected resolved material to be of type Material or ColorWithSubstrate, but got ${m}`)
        materialValue = m
    }

    return materialValue
}

export async function createPipingOrTapeParametric(
    type: "Piping" | "Tape",
    accessoryBaseId: string,
    allDescriptors: InterfaceDescriptor<unknown>[],
    context: ExtensionContext,
): Promise<PipingOrTape> {
    const materialInput = findSingleDescriptor(getDescriptorsOfType(allDescriptors, MaterialInfo), getPipingMaterialInputId(accessoryBaseId), "material input")
    const materialValue = await resolvePipingTapeMaterial(materialInput, context)

    const idPrefix = getAccessoryTemplateInputForBase(accessoryBaseId)
    const {templateInputId, templateId} = await resolveTemplateInput(idPrefix, allDescriptors, context)

    return {
        type,
        materialInput: fabricOrColorInput(materialInput.props.id, materialInput.props.name, materialValue, {
            allowQuilts: false,
            substrates: [
                {id: PIPING_SUBSTRATE_MATTE_ID, name: "Matte"},
                {id: PIPING_SUBSTRATE_GLOSSY_ID, name: "Glossy"},
            ],
        }),
        templateInputId,
        templateId,
    }
}

export async function createMeshWithInputs(
    assetType: Extract<AssetType, "Piping" | "Tape" | "Handle"> | "FabricLayer",
    element: TemplateNode,
    filteredDescriptors: InterfaceDescriptor<unknown>[],
    context: ExtensionContext,
): Promise<MeshWithInputs> {
    const materialInputs = getDescriptorsOfType(filteredDescriptors, MaterialInfo).filter((d) => d.props.type === "input")

    const resolvedInputs = await Promise.all(
        materialInputs.map(async (input): Promise<MaterialInput> => {
            const materialNode = getMaterialOrigin(input, context).value
            const value = materialNode ? await resolveSlotMaterialOrColor(input.props.id, materialNode, context) : undefined

            const isPipingOrTapeNamed = isPipingOrTapeMaterialInputName(input.props.name)
            if (isPipingOrTapeNamed || assetType === "Piping" || assetType === "Tape") {
                return pipingMaterialInput(input.props.id, input.props.name, value)
            }
            const fabricValue: Material | QuiltedMaterial | undefined = value && (isMaterial(value) || isQuiltedMaterial(value)) ? value : undefined
            if (assetType === "Handle") {
                return fabricInput(input.props.id, input.props.name, fabricValue && isMaterial(fabricValue) ? fabricValue : undefined, {allowQuilts: false})
            }
            return fabricInput(input.props.id, input.props.name, fabricValue, {allowQuilts: true})
        }),
    )

    const meshName = isNamedNode(element) ? element.parameters.name : ""
    const elementType = assetType === "FabricLayer" ? "Fabric" : assetType

    return {
        type: "MeshWithInputs",
        instanceId: element.instanceId,
        elementType,
        meshName,
        materialInputs: resolvedInputs,
    }
}

function pullerOnZipper(accessoryBaseId: string, pullerIndex: 0 | 1, allDescriptors: InterfaceDescriptor<unknown>[], context: ExtensionContext): boolean {
    const pullerTemplateInput = findSingleDescriptor(
        getDescriptorsOfType(allDescriptors, TemplateInfo),
        getPullerTemplateInputIdFromZipperBase(accessoryBaseId, pullerIndex),
        "puller template input",
    )
    const {setOnRoot: hasPuller} = getTemplateOrigin(pullerTemplateInput, context)

    return hasPuller
}

export async function createZipper(accessoryBaseId: string, allDescriptors: InterfaceDescriptor<unknown>[], context: ExtensionContext): Promise<Zipper> {
    const imageInput = findSingleDescriptor(getDescriptorsOfType(allDescriptors, ImageInfo), getZipperColorInputId(accessoryBaseId), "image input")

    let zipperMaterial: Color | undefined = undefined
    const imageNode = getImageOrigin(imageInput, context).value //color is passed in as ImageRGBA, the substrate is owned by the zipper
    if (imageNode) {
        const m = await resolveSlotMaterialOrColor(imageInput.props.id, imageNode, context)
        if (m && !isColor(m)) throw new Error(`Expected resolved zipper material to be of type Color, but got ${m}`)
        zipperMaterial = m
    }

    const idPrefix = getAccessoryTemplateInputForBase(accessoryBaseId)
    const {templateInputId, templateId} = await resolveTemplateInput(idPrefix, allDescriptors, context)

    let puller: Puller | undefined = undefined
    if (pullerOnZipper(accessoryBaseId, 0, allDescriptors, context)) {
        puller = await createPuller(getPullerBaseIdFromZipperBaseId(accessoryBaseId, 0), allDescriptors, context)
    }

    return {
        type: "Zipper",
        materialInput: colorInput(imageInput.props.id, imageInput.props.name, zipperMaterial),
        templateInputId,
        templateId,
        puller0TemplateInputId: getPullerTemplateInputIdFromZipperBase(accessoryBaseId, 0),
        puller,
    }
}

export async function createFabricLayer(
    name: string,
    filteredDescriptors: InterfaceDescriptor<unknown>[],
    allDescriptors: InterfaceDescriptor<unknown>[],
    layers: LayerData[] | undefined,
    context: ExtensionContext,
): Promise<FabricLayer> {
    const materialInput = getDescriptorsOfType(filteredDescriptors, MaterialInfo).filter((d) => d.props.type === "input")
    if (materialInput.length !== 1) throw new Error(`Expected exactly one material input for fabric layer, but found ${materialInput.length}`)

    let materialValue: Material | QuiltedMaterial | undefined = undefined
    const materialNode = getMaterialOrigin(materialInput[0], context).value
    if (materialNode) {
        const m = await resolveSlotMaterialOrColor(materialInput[0].props.id, materialNode, context)
        if (m && !isMaterial(m) && !isQuiltedMaterial(m))
            throw new Error(`Expected resolved fabric layer material to be of type Material or QuiltedMaterial, but got ${m}`)
        materialValue = m
    }

    let heightInputId: string | undefined = undefined
    let height: number | undefined = undefined
    if (layers) {
        const matchingLayer = layers.find((l) => l.parameterIds.includes(materialInput[0].props.id))
        if (matchingLayer) {
            heightInputId = matchingLayer.parameterIds.find((id) => id.endsWith("height"))
            if (heightInputId) {
                const heightDescriptor = getDescriptorsOfType(allDescriptors, NumberInfo).find((d) => d.props.id === heightInputId)
                if (heightDescriptor) height = (heightDescriptor.props.value as number) ?? undefined
            }
        }
    }

    return {
        type: "FabricLayer",
        name: name.length != 0 ? name : "Fabric",
        materialInput: fabricInput(materialInput[0].props.id, materialInput[0].props.name, materialValue, {allowQuilts: true}),
        heightInputId,
        height,
    }
}

export async function createHandle(handleBaseId: string, allDescriptors: InterfaceDescriptor<unknown>[], context: ExtensionContext): Promise<Handle> {
    const materialInputs = getDescriptorsOfType(allDescriptors, MaterialInfo).filter((d) => d.props.id.startsWith(handleBaseId) && d.props.type === "input")

    const idPrefix = getHandleTemplateInput(handleBaseId)
    const [resolvedMaterials, {templateInputId, templateId}] = await Promise.all([
        Promise.all(
            materialInputs.map(async (input): Promise<MaterialInput> => {
                const materialNode = getMaterialOrigin(input, context).value
                if (!materialNode) throw new Error(`Could not find material origin for handle material input ${input.props.id}`)
                const m = await resolveSlotMaterialOrColor(input.props.id, materialNode, context)
                if (!m) throw new Error(`Could not resolve handle material for input ${input.props.id}`)

                if (isPipingOrTapeMaterialInputName(input.props.name)) return pipingMaterialInput(input.props.id, input.props.name, m)
                else return fabricInput(input.props.id, input.props.name, isMaterial(m) ? m : undefined, {allowQuilts: false})
            }),
        ),
        resolveTemplateInput(idPrefix, allDescriptors, context),
    ])

    return {
        type: "Handle",
        materialInputs: resolvedMaterials,
        templateInputId,
        templateId,
    }
}

export async function createPuller(pullerBaseId: string, allDescriptors: InterfaceDescriptor<unknown>[], context: ExtensionContext): Promise<Puller> {
    const materialInputs = getDescriptorsOfType(allDescriptors, MaterialInfo).filter((d) => d.props.id.startsWith(pullerBaseId) && d.props.type === "input")
    const positionInput = findSingleDescriptor(getPullerPositionDescriptors(allDescriptors), getPullerPositionInputId(pullerBaseId), "position input")

    // A puller always lives inside a zipper. Resolve the owning zipper's template id so the puller can derive its size filter.
    const zipperBaseId = getZipperBaseIdFromPullerBase(pullerBaseId)
    const {templateId: zipperTemplateId} = await resolveTemplateInput(getAccessoryTemplateInputForBase(zipperBaseId), allDescriptors, context)

    const pullerTemplateInputId = getPullerTemplateInputIdFromPullerBase(pullerBaseId)
    const [resolvedMaterials, {templateInputId, templateId}] = await Promise.all([
        Promise.all(
            materialInputs.map(async (input): Promise<MaterialInput> => {
                const materialNode = getMaterialOrigin(input, context).value
                if (!materialNode) throw new Error(`Could not find material origin for puller material input ${input.props.id}`)
                const m = await resolveSlotMaterialOrColor(input.props.id, materialNode, context)
                if (!m) throw new Error(`Could not resolve puller material for input ${input.props.id}`)

                const value: ColorWithSubstrate | undefined = isColorWithSubstrate(m) ? m : undefined
                return colorInput(input.props.id, input.props.name, value, {substrates: [{id: PULLER_SUBSTRATE_ID, name: "Substrate"}]})
            }),
        ),
        resolveTemplateInput(pullerTemplateInputId, allDescriptors, context),
    ])

    return {
        type: "Puller",
        materialInputs: resolvedMaterials,
        positionInputId: positionInput.props.id,
        position: positionInput.props.value as number,
        templateInputId,
        templateId,
        zipperTemplateId,
    }
}
